import React from "react";
import { Link } from "react-router-dom";
import {
    LuArrowDownUp,
    LuArrowRight,
    LuCheck,
    LuCircleAlert,
    LuSettings,
    LuWaypoints,
} from "react-icons/lu";
import { useNavItems } from "./navbar/useNavItems";
import LandingBackdrop from "./LandingBackdrop";
import { useReveal } from "../hooks/useReveal";

/* Marketing page for the bridge. Every claim traces to something the transfer
   panel actually does — the route is priced before anything is signed, the
   route's cost is folded into that quote, the app never takes custody — and no
   figure is quoted that the app cannot show. In particular there is no headline
   fee percentage here, because the panel deliberately does not print one.

   The previews below are deliberately static reproductions of the real UI
   rather than screenshots: they follow the live theme, cost no image bytes, and
   cannot drift out of date the way a PNG does. Their amounts, chains and times
   are illustrative — the page makes no rate claim of its own. */

/* Plays a one-shot entrance once the block scrolls into view. `variant` picks
   the direction (see .reveal-* in index.css); `delay` staggers siblings. */
const Reveal = ({ as: Tag = "div", variant = "up", delay = 0, className = "", style, children, ...rest }) => {
    const [ref, visible] = useReveal();
    return (
        <Tag
            ref={ref}
            className={`reveal reveal-${variant} ${visible ? "is-visible" : ""} ${className}`}
            style={{ ...style, "--reveal-delay": `${delay}ms` }}
            {...rest}
        >
            {children}
        </Tag>
    );
};

const PREVIEW_SEND = "1,000.00";
const PREVIEW_RECEIVE = "998.71";

const TokenChip = ({ badge, symbol, tone }) => (
    <span className="token-pill-3d flex items-center gap-2 px-2.5 py-2 shrink-0">
        <span
            className={`size-[20px] rounded-full text-[9px] font-bold flex items-center justify-center ${tone}`}
        >
            {badge}
        </span>
        <span className="text-[13px] font-bold text-usdx-ink">{symbol}</span>
    </span>
);

const TransferPreview = () => (
    <div className="card-3d p-4 sm:p-5 w-full" aria-hidden="true">
        <div className="flex items-center justify-between gap-3 mb-4">
            <span className="text-[17px] font-bold gradient-text">Transfer</span>
            <span className="swap-arrow-3d w-8 h-8 flex items-center justify-center">
                <LuSettings className="size-[15px]" />
            </span>
        </div>

        <div className="inner-3d p-3.5">
            <div className="flex items-center justify-between mb-2.5">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-usdx-ink-subtle">
                    From
                </span>
                <span className="text-[10px] font-bold text-usdx-accent">Ethereum</span>
            </div>
            <div className="flex items-center justify-between gap-3">
                <span className="num text-[24px] sm:text-[26px] font-bold text-usdx-ink truncate">
                    {PREVIEW_SEND}
                </span>
                <TokenChip
                    badge="U"
                    symbol="USDC"
                    tone="bg-usdx-accent/20 text-usdx-accent"
                />
            </div>
        </div>

        <div className="flex justify-center py-2">
            <span className="swap-arrow-3d landing-swap w-10 h-10 flex items-center justify-center">
                <LuArrowDownUp className="size-[17px]" />
            </span>
        </div>

        <div className="inner-3d p-3.5">
            <div className="flex items-center justify-between mb-2.5">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-usdx-ink-subtle">
                    To
                </span>
                <span className="text-[10px] font-bold text-usdx-accent">Base</span>
            </div>
            <div className="flex items-center justify-between gap-3">
                <span className="num text-[24px] sm:text-[26px] font-bold text-usdx-ink truncate">
                    {PREVIEW_RECEIVE}
                </span>
                <TokenChip
                    badge="U"
                    symbol="USDC"
                    tone="bg-usdx-success/20 text-usdx-success"
                />
            </div>
        </div>

        <div className="flex items-center gap-2 px-1 py-3">
            <span className="landing-ping w-1.5 h-1.5 rounded-full bg-usdx-accent/50" />
            <span className="text-[11px] font-medium text-usdx-ink-subtle">
                One route · fees already in the quote
            </span>
        </div>

        <div className="btn-3d landing-sheen w-full py-3.5 text-[15px] font-bold text-center">
            Review transfer
        </div>
    </div>
);

const RoutePreview = () => (
    <div className="card-3d p-4 sm:p-5 w-full max-w-[380px]" aria-hidden="true">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-usdx-ink-subtle mb-3">
            Route details
        </p>
        <div className="flex flex-col">
            {[
                ["You receive", "998.71 USDC on Base", false],
                ["Route", "Best of those found", true],
                ["Route fees", "Inside the quote", false],
                ["Gas on arrival", "Included", false],
                ["Time to settle", "~2 min", false],
            ].map(([k, v, accent], i) => (
                <Reveal
                    key={k}
                    variant="right"
                    delay={150 + i * 70}
                    className="flex items-baseline justify-between gap-3 py-2.5 border-b border-usdx-line-soft last:border-b-0"
                >
                    <span className="text-[11px] text-usdx-ink-subtle font-medium shrink-0">
                        {k}
                    </span>
                    <span
                        className={`num text-[12px] font-semibold text-right truncate ${
                            accent ? "text-usdx-accent" : "text-usdx-ink"
                        }`}
                    >
                        {v}
                    </span>
                </Reveal>
            ))}
        </div>
        <p className="text-[10px] text-usdx-ink-subtle leading-relaxed mt-3">
            The figure on the To side is what the route is quoting to deliver — its
            fees are already taken out of it.
        </p>
    </div>
);

const TRANSFER_STEPS = [
    { t: "Allowance approved", d: "Ethereum", state: "done" },
    { t: "Sent on the source chain", d: "Ethereum", state: "done" },
    { t: "Waiting for finality", d: "In progress", state: "live" },
    { t: "Arrives on Base", d: "Pending", state: "next" },
];

const StatusPreview = () => (
    <div className="card-3d p-4 sm:p-5 w-full max-w-[380px]" aria-hidden="true">
        <div className="flex items-center justify-between gap-3 mb-3">
            <span className="text-[15px] font-bold text-usdx-ink">Transfer status</span>
            <span className="token-pill-3d px-3 py-1 text-[11px] font-bold text-usdx-accent">
                In flight
            </span>
        </div>
        <div className="landing-progress mb-3" />
        <div className="rounded-2xl border border-usdx-line bg-usdx-surface overflow-hidden">
            {TRANSFER_STEPS.map(({ t, d, state }, i) => (
                <Reveal
                    key={t}
                    variant="up"
                    delay={150 + i * 90}
                    className={`flex items-center gap-3 px-3.5 py-2.5 border-b border-usdx-line-soft last:border-b-0 ${
                        state === "live" ? "option-selected" : ""
                    }`}
                >
                    <span
                        className={`size-[18px] rounded-full flex items-center justify-center shrink-0 ${
                            state === "done"
                                ? "bg-usdx-success/20 text-usdx-success"
                                : state === "live"
                                  ? "bg-usdx-accent/15 text-usdx-accent"
                                  : "bg-usdx-surface-sunken text-usdx-ink-subtle"
                        }`}
                    >
                        {state === "done" ? (
                            <LuCheck className="size-[11px]" />
                        ) : (
                            <span
                                className={`w-1.5 h-1.5 rounded-full bg-current ${
                                    state === "live" ? "landing-ping" : ""
                                }`}
                            />
                        )}
                    </span>
                    <span className="flex-1 min-w-0">
                        <span
                            className={`block text-[12px] font-semibold truncate ${
                                state === "next" ? "text-usdx-ink-subtle" : "text-usdx-ink"
                            }`}
                        >
                            {t}
                        </span>
                    </span>
                    <span className="text-[10px] text-usdx-ink-subtle shrink-0">{d}</span>
                </Reveal>
            ))}
        </div>
        <p className="text-[10px] text-usdx-ink-subtle leading-relaxed mt-3">
            The panel keeps tracking it until the destination chain releases — leave it
            open rather than sending again.
        </p>
    </div>
);

/* Mirrors the "This page will never" note carried inside the transfer panel. */
const CUSTODY = [
    "Never holds your tokens, at any point",
    "Never asks for a seed phrase or private key",
    "Never moves funds without a signature from you",
];

const CustodyPreview = () => (
    <div className="card-3d p-4 sm:p-5 w-full max-w-[380px]" aria-hidden="true">
        <p className="text-[13px] font-bold text-usdx-ink mb-1">Custody stays with you</p>
        <p className="text-[11px] text-usdx-ink-subtle leading-relaxed mb-3.5">
            The transfer is built for you, but nothing moves until your wallet signs it.
        </p>
        <div className="rounded-2xl border border-usdx-line bg-usdx-surface overflow-hidden">
            {CUSTODY.map((line, i) => (
                <Reveal
                    key={line}
                    variant="right"
                    delay={150 + i * 90}
                    className="flex items-start gap-2.5 px-3.5 py-2.5 border-b border-usdx-line-soft last:border-b-0"
                >
                    <LuCircleAlert className="size-[14px] text-usdx-accent shrink-0 mt-0.5" />
                    <span className="text-[11px] text-usdx-ink leading-relaxed">{line}</span>
                </Reveal>
            ))}
        </div>
        <div className="mt-3 p-2.5 rounded-xl border bg-usdx-success/10 border-usdx-success/25 flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-usdx-success shrink-0 mt-1.5" />
            <p className="text-[11px] font-medium text-usdx-success-strong leading-relaxed">
                Non-custodial throughout. Your keys never leave your wallet, and every
                step is yours to approve.
            </p>
        </div>
    </div>
);

/* Alternating copy/visual row — the device that makes this read as a landing
   page rather than another grid of equal cards. */
const FeatureRow = ({ eyebrow, title, body, points, visual, flip }) => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
        <Reveal variant={flip ? "right" : "left"} className={`text-left ${flip ? "lg:order-2" : ""}`}>
            <p className="landing-eyebrow mb-2.5">{eyebrow}</p>
            <h3 className="text-[24px] sm:text-[30px] lg:text-[34px] font-extrabold text-usdx-ink leading-[1.12] tracking-tight">
                {title}
            </h3>
            <p className="text-[14px] sm:text-[15px] text-usdx-ink-muted leading-relaxed mt-3.5 max-w-[520px]">
                {body}
            </p>
            <ul className="flex flex-col gap-2.5 mt-5">
                {points.map((p, i) => (
                    <Reveal as="li" key={p} delay={200 + i * 80} className="flex items-start gap-2.5">
                        <span className="size-[18px] rounded-full bg-usdx-accent/12 text-usdx-accent flex items-center justify-center shrink-0 mt-0.5">
                            <LuCheck className="size-[11px]" />
                        </span>
                        <span className="text-[13px] text-usdx-ink-soft leading-relaxed">{p}</span>
                    </Reveal>
                ))}
            </ul>
        </Reveal>
        <Reveal
            variant="scale"
            delay={120}
            className={`flex justify-center ${flip ? "lg:order-1 lg:justify-start" : "lg:justify-end"}`}
        >
            {visual}
        </Reveal>
    </div>
);

const STEPS = [
    {
        n: "01",
        t: "Connect a wallet",
        d: "The transfer panel carries its own wallet menu. Connect there and you are ready — there is nothing to sign up for.",
    },
    {
        n: "02",
        t: "Choose both sides",
        d: "Pick the chain and token you are sending, and the chain and token you want to end up with. The route is found and priced for you.",
    },
    {
        n: "03",
        t: "Approve and confirm",
        d: "A one-off allowance for the token, then the transfer itself. After that it is a matter of waiting for the other side to release.",
    },
];

const FAQS = [
    {
        q: "What does bridging actually do?",
        a: "It moves value from one chain to another. A route can change the token along the way as well, so one run can leave you on a different network holding a different asset.",
    },
    {
        q: "What does it cost?",
        a: "Gas on the source chain, which is an ordinary transaction priced in that chain's native token, plus whatever the route itself charges. The route's cost is already folded into the amount you are quoted, so the figure on screen is what the transfer is expected to deliver.",
    },
    {
        q: "How long does it take?",
        a: "Usually minutes. Most of that is waiting for the source chain to finalise before the other side releases, so it varies by chain and by route.",
    },
    {
        q: "Will I have gas on the destination chain?",
        a: "Some routes deliver a little native token along with the transfer. Where they cannot, you will need some on that chain before you can move the funds again.",
    },
    {
        q: "The route changed when I went to confirm.",
        a: "Quotes are priced when they are fetched and liquidity moves underneath them. Refetch, read the new output amount, and confirm from that.",
    },
    {
        q: "It is still pending — where are my tokens?",
        a: "Bridges wait for the source chain to finalise before releasing on the other side, and that wait differs by chain. Open the transfer from the panel's history and follow the destination transaction on that chain's explorer; some routes settle in two steps, so the second may still be in flight.",
    },
    {
        q: "Do you ever hold my funds?",
        a: "No. The app never takes custody, never asks for a seed phrase or a private key, and cannot move anything without a signature from your wallet.",
    },
];

const Landing = () => {
    const ecosystem = useNavItems();

    return (
        <div className="w-full text-left">

            {/* ══ Hero ══════════════════════════════════════════════════ */}
            <section className="relative w-full pt-4 sm:pt-8 lg:pt-12">
                <div className="landing-glow" aria-hidden="true" />
                <LandingBackdrop />

                <div className="relative z-10 mx-auto max-w-[1180px] grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] gap-10 lg:gap-14 items-center">
                    <div className="text-left">
                        <span className="token-pill-3d inline-flex items-center gap-2 px-3 py-1.5 rise rise-1">
                            <span
                                className="landing-ping landing-ping-success w-1.5 h-1.5 rounded-full bg-usdx-success"
                                aria-hidden="true"
                            />
                            <span className="text-[11px] font-bold text-usdx-ink-muted">
                                Live · cross-chain transfers
                            </span>
                        </span>

                        <h1 className="text-[34px] xs:text-[40px] sm:text-[52px] lg:text-[60px] font-extrabold text-usdx-ink leading-[1.04] tracking-tight mt-5 rise rise-2">
                            Move it to another chain
                            <br />
                            <span className="gradient-text landing-shimmer">in a single run.</span>
                        </h1>

                        <p className="text-[15px] sm:text-[17px] text-usdx-ink-muted leading-relaxed mt-5 max-w-[520px] rise rise-3">
                            USDX BridgeX carries funds across chains. Say where they are and
                            where they should land — the route is found, priced and shown to
                            you before your wallet signs anything.
                        </p>

                        <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-3 mt-8 rise rise-4">
                            <Link
                                to="/bridge"
                                className="btn-3d landing-sheen landing-cta py-4 px-7 text-[15px] font-bold inline-flex items-center justify-center gap-2"
                            >
                                Start a transfer
                                <LuArrowRight className="size-[17px]" aria-hidden="true" />
                            </Link>
                            <Link
                                to="/ecosystem"
                                className="btn-outline-3d py-4 px-7 text-[15px] font-bold inline-flex items-center justify-center"
                            >
                                Explore USDX Smart
                            </Link>
                        </div>

                        <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-7 rise rise-5">
                            {["Non-custodial", "No sign-up", "Fees inside the quote"].map((t) => (
                                <li
                                    key={t}
                                    className="flex items-center gap-1.5 text-[12px] font-semibold text-usdx-ink-muted"
                                >
                                    <LuCheck
                                        className="size-[13px] text-usdx-success"
                                        aria-hidden="true"
                                    />
                                    {t}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="w-full max-w-[420px] mx-auto lg:mx-0 rise rise-3">
                        <div className="landing-float">
                            <div className="landing-mock">
                                <TransferPreview />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ══ Suite strip ═══════════════════════════════════════════ */}
            <section className="relative mx-auto max-w-[1180px] mt-14 sm:mt-20">
                <Reveal className="card-3d px-4 py-4 sm:px-6 sm:py-5 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
                    <p className="landing-eyebrow shrink-0">Part of USDX Smart</p>
                    <div className="flex flex-wrap items-center gap-2 lg:gap-2.5">
                        {ecosystem.map(({ label, href, icon: Icon, comingSoon }) =>
                            comingSoon ? (
                                <span
                                    key={label}
                                    aria-disabled="true"
                                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-[12px] font-bold text-usdx-ink-subtle/70"
                                >
                                    <Icon className="size-[15px]" aria-hidden="true" />
                                    {label}
                                    <span className="rounded-full bg-usdx-surface-sunken px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-usdx-ink-subtle">
                                        Soon
                                    </span>
                                </span>
                            ) : (
                                <a
                                    key={label}
                                    href={href}
                                    className="token-pill-3d inline-flex items-center gap-2 px-3 py-2 text-[12px] font-bold text-usdx-ink"
                                >
                                    <Icon
                                        className="size-[15px] text-usdx-accent"
                                        aria-hidden="true"
                                    />
                                    {label}
                                </a>
                            ),
                        )}
                    </div>
                </Reveal>
            </section>

            {/* ══ Feature rows ══════════════════════════════════════════ */}
            <section className="mx-auto max-w-[1180px] flex flex-col gap-16 sm:gap-24 mt-16 sm:mt-24">
                <FeatureRow
                    eyebrow="Priced before you sign"
                    title="The route is quoted first, then shown to you"
                    body="Choose the two sides and the route is found and priced before your wallet is asked for anything. What the transfer is expected to deliver, what it will cost and how long it should take are all on screen first."
                    points={[
                        "Route fees are folded into the amount quoted, not added afterwards",
                        "One run can cross both the chain and the token",
                        "Some routes deliver a little native token so you can move on arrival",
                    ]}
                    visual={
                        <div className="landing-mock w-full flex justify-center lg:justify-end">
                            <RoutePreview />
                        </div>
                    }
                />
                <FeatureRow
                    flip
                    eyebrow="Watch it land"
                    title="Followed from approval to arrival"
                    body="Most of the wait is the source chain finalising, so it varies by chain and by route. The transfer is tracked the whole way, with each side's transaction one click from its explorer."
                    points={[
                        "Progress shown step by step rather than a single spinner",
                        "History keeps every transfer, including routes that settle in two steps",
                        "If your wallet is on the wrong network, it is asked to switch first",
                    ]}
                    visual={
                        <div className="landing-mock w-full flex justify-center lg:justify-start">
                            <StatusPreview />
                        </div>
                    }
                />
                <FeatureRow
                    eyebrow="Non-custodial"
                    title="Your wallet holds the funds the whole way"
                    body="Nothing is pooled, escrowed or held on your behalf. The transaction is built for you to inspect, and it only moves once you sign it in your own wallet."
                    points={[
                        "No account and no sign-up — connecting a wallet is the whole of it",
                        "A seed phrase or private key is never asked for, anywhere",
                        "Nothing moves without a signature from you",
                    ]}
                    visual={
                        <div className="landing-mock w-full flex justify-center lg:justify-end">
                            <CustodyPreview />
                        </div>
                    }
                />
            </section>

            {/* ══ Steps ═════════════════════════════════════════════════ */}
            <section className="mx-auto max-w-[1180px] mt-16 sm:mt-24">
                <Reveal className="text-center max-w-[620px] mx-auto">
                    <p className="landing-eyebrow mb-2.5">Getting started</p>
                    <h2 className="text-[26px] sm:text-[34px] font-extrabold text-usdx-ink leading-[1.12] tracking-tight">
                        Three steps, then it is on its way
                    </h2>
                </Reveal>
                {/* Three across only from md: at 640px each card was ~190px wide */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mt-9">
                    {STEPS.map(({ n, t, d }, i) => (
                        <Reveal
                            key={n}
                            delay={i * 120}
                            className="card-3d landing-lift p-5 sm:p-6 text-left"
                        >
                            <p
                                className="landing-step-index text-[40px] sm:text-[46px] mb-2"
                                aria-hidden="true"
                            >
                                {n}
                            </p>
                            <h3 className="text-[16px] font-bold text-usdx-ink">{t}</h3>
                            <p className="text-[13px] text-usdx-ink-subtle leading-relaxed mt-2">
                                {d}
                            </p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* ══ FAQ ═══════════════════════════════════════════════════ */}
            <section className="mx-auto max-w-[820px] mt-16 sm:mt-24">
                <Reveal className="text-center mb-8">
                    <p className="landing-eyebrow mb-2.5">Questions</p>
                    <h2 className="text-[26px] sm:text-[34px] font-extrabold text-usdx-ink leading-[1.12] tracking-tight">
                        Before you bridge
                    </h2>
                </Reveal>
                <div className="flex flex-col gap-2.5">
                    {FAQS.map(({ q, a }, i) => (
                        /* <details> rather than a JS accordion — keyboard support,
                           find-in-page and printing all come for free. */
                        <Reveal
                            as="details"
                            key={q}
                            delay={Math.min(i, 4) * 60}
                            className="card-3d landing-faq px-4 py-3.5 sm:px-5 text-left group"
                        >
                            <summary className="text-[14px] font-bold text-usdx-ink cursor-pointer list-none flex items-center justify-between gap-4">
                                {q}
                                <span
                                    className="text-usdx-ink-subtle text-[20px] leading-none shrink-0 transition-transform duration-200 group-open:rotate-45"
                                    aria-hidden="true"
                                >
                                    +
                                </span>
                            </summary>
                            <p className="landing-faq-answer text-[13px] text-usdx-ink-subtle leading-relaxed mt-2.5 max-w-[680px]">
                                {a}
                            </p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* ══ Closing band ══════════════════════════════════════════ */}
            <section className="mx-auto max-w-[1180px] mt-16 sm:mt-24">
                <Reveal variant="scale" className="landing-band px-5 py-10 xs:px-6 sm:px-12 sm:py-16 text-center">
                    <h2 className="text-[26px] sm:text-[38px] font-extrabold leading-[1.08] tracking-tight max-w-[620px] mx-auto">
                        Connect a wallet and move something
                    </h2>
                    <p className="text-[14px] sm:text-[15px] leading-relaxed mt-4 max-w-[480px] mx-auto opacity-90">
                        The transfer panel asks for a wallet only when a step actually needs
                        one. Nothing to install beyond the one you already use.
                    </p>
                    <div className="flex flex-col xs:flex-row items-stretch xs:items-center justify-center gap-3 mt-8">
                        <Link
                            to="/bridge"
                            className="btn-on-accent py-4 px-7 text-[15px] font-bold inline-flex items-center justify-center gap-2"
                        >
                            Open the bridge
                            <LuArrowRight className="size-[17px]" aria-hidden="true" />
                        </Link>
                        <Link
                            to="/ecosystem"
                            className="btn-on-accent-ghost py-4 px-7 text-[15px] font-bold inline-flex items-center justify-center"
                        >
                            See the suite
                        </Link>
                    </div>
                </Reveal>
            </section>

            {/* ══ Footer ════════════════════════════════════════════════ */}
            <Reveal as="footer" className="mx-auto max-w-[1180px] mt-16 sm:mt-20 pt-8 pb-6 border-t border-usdx-line">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-6">
                    <div className="text-left">
                        <div className="flex items-center gap-2 mb-3">
                            <img
                                src="/usdx-icon.png"
                                alt=""
                                aria-hidden="true"
                                className="h-8 w-8"
                            />
                            <span className="flex flex-col leading-none">
                                <span
                                    style={{ color: "#0d8ce0" }}
                                    className="text-[17px] font-extrabold tracking-tight"
                                >
                                    USDX
                                </span>
                                <span
                                    style={{ color: "#2b9be8" }}
                                    className="mt-1 flex w-full justify-between text-[8px] font-semibold uppercase"
                                    aria-hidden="true"
                                >
                                    {["S", "M", "A", "R", "T"].map((l) => (
                                        <span key={l}>{l}</span>
                                    ))}
                                </span>
                            </span>
                        </div>
                        <p className="text-[12px] text-usdx-ink-subtle leading-relaxed max-w-[260px]">
                            Cross-chain transfers for the USDX Smart suite. Non-custodial:
                            your funds move only when you sign for them.
                        </p>
                    </div>

                    <div className="text-left">
                        <p className="text-[11px] font-bold uppercase tracking-widest text-usdx-ink-subtle mb-3">
                            This app
                        </p>
                        <ul className="flex flex-col gap-2">
                            {[
                                ["Bridge", "/bridge"],
                                ["Ecosystem", "/ecosystem"],
                            ].map(([label, to]) => (
                                <li key={to}>
                                    <Link
                                        to={to}
                                        className="text-[13px] font-medium text-usdx-ink-muted hover:text-usdx-accent transition-colors"
                                    >
                                        {label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="text-left">
                        <p className="text-[11px] font-bold uppercase tracking-widest text-usdx-ink-subtle mb-3">
                            USDX apps
                        </p>
                        <ul className="flex flex-col gap-2">
                            {ecosystem.map(({ label, href, comingSoon }) => (
                                <li key={label}>
                                    {comingSoon ? (
                                        <span className="text-[13px] font-medium text-usdx-ink-subtle/70">
                                            {label} · soon
                                        </span>
                                    ) : (
                                        <a
                                            href={href}
                                            className="text-[13px] font-medium text-usdx-ink-muted hover:text-usdx-accent transition-colors"
                                        >
                                            {label}
                                        </a>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-8 pt-5 border-t border-usdx-line">
                    <p className="text-[11px] text-usdx-ink-subtle">
                        <LuWaypoints
                            className="inline size-[12px] mr-1.5 -mt-0.5"
                            aria-hidden="true"
                        />
                        Routes are priced when they are fetched — confirm from the amount on
                        screen.
                    </p>
                    <p className="text-[11px] text-usdx-ink-subtle">
                        Cross-chain transfers carry risk, including delays on the
                        destination chain.
                    </p>
                </div>
            </Reveal>
        </div>
    );
};

export default Landing;
